import React, { createContext, useEffect, useState, useCallback } from 'react';
import { useApi } from '../services/api';
import { useDispatch } from 'react-redux';
import { setUserRoles } from '../actions/authActions';
import { auth } from "../firebase";
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider, 
  onAuthStateChanged,
  signInWithCredential 
} from "firebase/auth";
import { useNavigate } from 'react-router-dom';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { fetchData } = useApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isTokenRefreshing, setIsTokenRefreshing] = useState(true);
  const [jwtToken, setJwtToken] = useState(() => localStorage.getItem("jwtToken"));

  const isValidDate = (dateString) => {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };

  const getToken = useCallback(async () => {
    let currToken = localStorage.getItem('jwtToken');
    const user = auth.currentUser;

    if (currToken && !user) {
      return currToken;
    }

    try {
      console.log('🔑🔑Current user:', user);
      if (!user) return null;

      let currExpirationTime = localStorage.getItem('expirationTime');
      console.log('🔑🔑Current expiration time:', currExpirationTime);

      if (!isValidDate(currExpirationTime) || !currToken) {
        const tokenResult = await user.getIdTokenResult();

        localStorage.setItem('expirationTime', tokenResult.expirationTime);
        localStorage.setItem('jwtToken', tokenResult.token);
        setJwtToken(tokenResult.token);

        currExpirationTime = tokenResult.expirationTime;
        currToken = tokenResult.token;
      };

      const expirationTime = new Date(currExpirationTime).getTime();
      const now = new Date().getTime();

      console.log('🔑⏰ Remaining time until token expiration:', Math.round((expirationTime - now) / (60 * 1000)), 'minutes');
  
      // If token will expire in 5 minutes, force refresh token
      if (expirationTime - now < 10 * 60 * 1000) {
        console.log('🔑🔑Refreshing token');
        // Force refresh token
        const newToken = await user.getIdToken(true);
        localStorage.setItem('expirationTime', newToken.expirationTime);
        localStorage.setItem('jwtToken', newToken);
        setJwtToken(newToken);
        currToken = newToken;
      }

      return currToken;
    } catch (error) {
      console.error('Token check error:', error);
      return null;
    }
  }, []);

  const handleUserData = useCallback(async (token, lang) => {
    setIsLoading(true);
    try {
      const response = await fetchData('/api/user/me', {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.data) {
        dispatch(setUserRoles(response.data.roles));
        localStorage.setItem('user_id', response.data.id);
        localStorage.setItem('name', response.data.name);
        localStorage.setItem('jwtToken', token);
        localStorage.setItem("lang", lang);
        localStorage.setItem('user_email', response.data.email);
        localStorage.setItem('client_name', response.data.client_name);
        localStorage.setItem('clients', JSON.stringify(response.data.clients));
        localStorage.setItem('default_logging_mode', response.data.logging_mode === 1 ? 'continuous' : 'sequential');
      }
      
      return response;
    } catch (error) {
      const errMsg = error?.response?.data?.error || error.message || 'Something went wrong'

      if (errMsg === 'You do not have permission to access this resource') {
        auth.signOut().then(() => {
          localStorage.clear();
        });
      }

      setError(errMsg);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [fetchData, dispatch]);

  const handleNativeGoogleAuth = useCallback(async (token) => {
    console.log('🔑👨Native auth handler starting with token:', token ? 'Present' : 'Absent');
    console.log('🔑👨Current Firebase user:', auth.currentUser?.email);
    console.log('🔑👨Current localStorage token:', localStorage.getItem('jwtToken') ? 'Present' : 'Absent');
    
    setIsLoading(true);
    try {
      const lang = localStorage.getItem("lang") || "id";
      setJwtToken(token);
      localStorage.setItem("jwtToken", token);
      await handleUserData(token, lang);
      console.log('User data handled successfully');
      return token;
    } catch (error) {
      console.error('Native auth error:', error);
      setJwtToken(null);
      localStorage.removeItem("jwtToken");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [handleUserData]);

  useEffect(() => {
    const initializeAuth = async () => {
      setIsTokenRefreshing(true);
      try {
        const storedToken = localStorage.getItem('jwtToken');
        const currentUser = auth.currentUser;
  
        if (storedToken && currentUser) {
          // If we have both stored token and Firebase user, get fresh token
          const freshToken = await currentUser.getIdToken(true);
          console.log('Refreshing token during init');
          setJwtToken(freshToken);
          localStorage.setItem('jwtToken', freshToken);
          
          const lang = localStorage.getItem("lang") || "id";
          await handleUserData(freshToken, lang);
        } else if (storedToken) {
          // Only stored token (like from native auth), try to use it
          console.log('Using stored token for init');
          setJwtToken(storedToken);
          const lang = localStorage.getItem("lang") || "id";
          await handleUserData(storedToken, lang);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setJwtToken(null);
        localStorage.removeItem('jwtToken');
      } finally {
        setIsTokenRefreshing(false);
      }
    };

    if (!jwtToken || !localStorage.getItem('jwtToken')) {
      initializeAuth();
    }
  }, [handleUserData, jwtToken]);

  useEffect(() => {
    const completeNativeAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const accessToken = params.get('access_token');
      const idToken = params.get('id_token');
  
      if (token && accessToken && idToken) {
        try {
          await handleNativeGoogleAuth(token);
          const credential = GoogleAuthProvider.credential(idToken, accessToken);
          await signInWithCredential(auth, credential);
          navigate('/home');
        } catch (error) {
          console.error('Auth completion error:', error);
          localStorage.removeItem('jwtToken'); 
          navigate('/login');
        }
      }
    };
  
    completeNativeAuth();
  }, [handleNativeGoogleAuth, navigate]);

  useEffect(() => {
    const handleNativeAuth = async (event) => {
      const { token } = event.detail;
      try {
        await handleNativeGoogleAuth(token);
        navigate('/home');
      } catch (error) {
        console.error('Native auth error:', error);
        setError(error.message);
      }
    };
  
    window.addEventListener('nativeAuth', handleNativeAuth);
    return () => window.removeEventListener('nativeAuth', handleNativeAuth);
  }, [handleNativeGoogleAuth, navigate]);
  
  useEffect(() => {
    console.log('🔑🔑JWT Token changed:🔑🔑', jwtToken ? 'Present' : 'Absent');
  }, [jwtToken]);

  useEffect(() => {
    console.log('Setting up Firebase auth listener');
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsTokenRefreshing(true);
      try {
        if (user) {
          console.log('Firebase user detected, refreshing token');
          const token = await getToken();
          const lang = localStorage.getItem("lang") || "id";
          handleUserData(token, lang);
        }
      } catch (error) {
        console.error('Token refresh error:', error);
      } finally {
        setIsTokenRefreshing(false);
      }
    });

    return () => unsubscribe();
  }, [handleUserData, getToken]);

  const handleEmailAuth = async (email, password, lang) => {
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken();
      await handleUserData(token, lang);
      return token;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleWebGoogleAuth = async (lang) => {
    setIsLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const token = await userCredential.user.getIdToken();
      const user = userCredential.user;
      if (user?.photoURL) {
        localStorage.setItem('profilePicture', user.photoURL);
      }
      try {
        await fetchData('/check-user', {
          params : {
            "email": user?.email
          },
        });
        await handleUserData(token, lang);
      } catch (error) {
        if (error.response?.data?.error === "Failed to check email : user not found") {
          navigate("/signup-google", { state: { userEmail: user.email, userName: user.displayName } });
        }
        throw error;
      }
      return token;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      handleEmailAuth,
      handleWebGoogleAuth,
      handleNativeGoogleAuth,
      error,
      setError,
      isLoading,
      jwtToken,
      isTokenRefreshing,
      getToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};